import { useMemo, useState } from 'react';
import { TranscriptionBlockData } from '../lib/transcription.types';
import {
  buildTranscriptionSearchMatches,
  effectiveNormalizedSearchQuery,
  filterTranscriptionBlocksBySearchQuery,
} from './transcription-search-utils';

/**
 * Client-side transcript search: filtered blocks, matching word ids for highlights, and query state.
 * Uses `effectiveNormalizedSearchQuery`: shorter trimmed queries do not filter or highlight.
 */
export function useTranscriptionSearch(blocks: TranscriptionBlockData[]) {
  const [query, setQuery] = useState('');

  const normalizedQuery = useMemo(() => effectiveNormalizedSearchQuery(query.trim()), [query]);

  const matches = useMemo(
    () => buildTranscriptionSearchMatches(blocks, normalizedQuery),
    [blocks, normalizedQuery],
  );

  const matchWordIds = useMemo(() => new Set(matches.map((m) => m.wordId)), [matches]);

  const displayBlocks = useMemo(
    () => filterTranscriptionBlocksBySearchQuery(blocks, normalizedQuery),
    [blocks, normalizedQuery],
  );

  return {
    query,
    setQuery,
    normalizedQuery,
    matches,
    matchCount: matches.length,
    matchWordIds,
    displayBlocks,
    noMatches: normalizedQuery.length > 0 && displayBlocks.length === 0,
  };
}
